import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Tag, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, imgSrc, errMsg, fmtPrice, availabilityLabel } from "../../lib/api";

export default function WorksList({ artworks, onEdit, onDeleted }) {
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    if (!pending || busy) return;
    setBusy(true);
    try {
      await api.delete(`/artworks/${pending.id}`);
      toast.success("Removed from the collection");
      setPending(null);
      onDeleted();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="works-list" className="border border-line bg-panel p-6 md:p-8 lg:sticky lg:top-28">
      <p className="eyebrow !text-ochre/90">The Collection</p>
      <h2 className="font-display text-2xl mt-2">Manage works</h2>

      {artworks.length === 0 ? (
        <p className="text-sm text-mute mt-6">No works yet — add your first piece with the form.</p>
      ) : (
        <ul className="mt-6 divide-y divide-white/5">
          {artworks.map((a) => (
            <li key={a.id} className="py-4 flex items-center gap-4" data-testid={`work-row-${a.id}`}>
              <img
                src={imgSrc(a.image_path)}
                alt={a.title}
                loading="lazy"
                decoding="async"
                className="h-14 w-14 object-cover border border-line shrink-0"
              />
              <div className="min-w-0 flex-1">
                <p className="font-display text-lg leading-tight truncate">
                  {a.title}
                  {a.sold && <span className="ml-2 align-middle bg-oxblood text-bone font-meta text-[8px] uppercase tracking-[0.2em] px-1.5 py-0.5" data-testid={`work-sold-tag-${a.id}`}>Sold</span>}
                </p>
                <p className="font-meta text-[9px] uppercase tracking-[0.2em] text-mute mt-1 truncate">
                  {[a.medium, a.dimensions].filter(Boolean).join(" · ") || "—"}
                </p>
                <p className="font-meta text-[9px] uppercase tracking-[0.2em] mt-1">
                  <span className="text-gold">{a.price ? fmtPrice(a.price) : "—"}</span>
                  <span className="text-mute"> · {availabilityLabel(a.availability)}</span>
                </p>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button
                  onClick={async () => {
                    try {
                      await api.put(`/artworks/${a.id}`, { sold: !a.sold });
                      toast.success(a.sold ? "Marked as available" : "Marked as sold");
                      onDeleted();
                    } catch (err) {
                      toast.error(errMsg(err));
                    }
                  }}
                  data-testid={`work-sold-toggle-${a.id}`}
                  aria-label={a.sold ? "Mark as available" : "Mark as sold"}
                  className={`p-2 border transition-colors ${a.sold ? "border-ochre/50 text-ochre" : "border-line text-mute hover:text-ochre hover:border-ochre/50"}`}
                >
                  <Tag size={13} />
                </button>
                <button
                  onClick={() => onEdit(a)}
                  data-testid={`work-edit-button-${a.id}`}
                  className="p-2 border border-line text-mute hover:text-gold hover:border-gold/50 transition-colors"
                  aria-label={`Edit ${a.title}`}
                >
                  <Pencil size={13} />
                </button>
                <button
                  onClick={() => setPending(a)}
                  data-testid={`work-delete-button-${a.id}`}
                  className="p-2 border border-line text-mute hover:text-red-400 hover:border-red-400/50 transition-colors"
                  aria-label={`Delete ${a.title}`}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent className="bg-panel border-line max-w-md" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl font-light">
              Remove “{pending?.title}”?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-mute text-sm">
              This permanently removes the work and its photo from the public collection.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-cancel-button"
              className="border-line bg-transparent text-bone hover:bg-elevated"
            >
              Keep
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={remove}
              disabled={busy}
              data-testid="delete-confirm-button"
              className="bg-red-600/90 text-white hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
